import { createTheme } from '@mui/material/styles';

// BRUTALIST RETRO color palette - richer, sleeker
const brutalistColors = {
  dark: {
    primary: '#ff3366',     // Vibrant pink/coral
    secondary: '#00e5cc',   // Rich cyan/teal
    tertiary: '#f0ff00',    // Electric yellow
    accent: '#ff7744',      // Warm coral
  },
  light: {
    primary: '#e6005c',     // Deep magenta
    secondary: '#0088aa',   // Deep teal
    tertiary: '#44cc00',    // Rich green
    accent: '#ff5500',      // Burnt orange
  },
};

// Status colors - bold and saturated
const statusColors = {
  success: { dark: '#00ff00', light: '#00cc00' },
  warning: { dark: '#ffff00', light: '#ffcc00' },
  error: { dark: '#ff0000', light: '#cc0000' },
  info: { dark: '#00ffff', light: '#0099ff' },
};

const getTheme = (themeMode) => {
  const isDark = themeMode === 'dark';
  const colors = isDark ? brutalistColors.dark : brutalistColors.light;

  return createTheme({
    palette: {
      mode: themeMode,
      background: {
        default: isDark ? '#0d0d1a' : '#c0c0c0',
        paper: isDark ? '#141428' : '#e0e0e0',
        actions: isDark ? '#0a0a14' : '#b0b0b0',
        highlight: isDark ? 'rgba(255,46,99,0.15)' : 'rgba(255,0,128,0.1)',
        overlay: isDark ? 'rgba(0,0,0,0.85)' : 'rgba(192,192,192,0.9)',
        modal: isDark ? '#0a0a14' : '#c0c0c0',
        elevated: isDark ? '#1a1a30' : '#e8e8e8',
        sidebar: isDark ? '#0a0a14' : '#b8b8b8',
        card: isDark ? '#141428' : '#e0e0e0',
        hover: isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)',
      },
      primary: {
        main: colors.primary,
        light: colors.primary,
        dark: colors.primary,
      },
      secondary: {
        main: colors.secondary,
      },
      accent: {
        primary: colors.primary,
        secondary: colors.secondary,
        tertiary: colors.tertiary,
      },
      success: {
        main: isDark ? statusColors.success.dark : statusColors.success.light,
      },
      warning: {
        main: isDark ? statusColors.warning.dark : statusColors.warning.light,
      },
      error: {
        main: isDark ? statusColors.error.dark : statusColors.error.light,
      },
      info: {
        main: isDark ? statusColors.info.dark : statusColors.info.light,
      },
      text: {
        primary: isDark ? '#ffffff' : '#000000',
        secondary: isDark ? 'rgba(255,255,255,0.6)' : 'rgba(0,0,0,0.55)',
        disabled: isDark ? 'rgba(255,255,255,0.35)' : 'rgba(0,0,0,0.35)',
      },
      divider: isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)',
      border: {
        main: isDark ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.12)',
        highlight: colors.primary,
        subtle: isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)',
      },
    },
    shape: {
      borderRadius: 6,
      borderRadiusLg: 8,
      borderRadiusSm: 4,
    },
    typography: {
      fontFamily: "'Space Mono', monospace",
      h1: {
        fontFamily: "'Space Mono', monospace",
        fontSize: '1.5rem',
        fontWeight: 700,
        letterSpacing: '0.08em',
        lineHeight: 1.3,
        textTransform: 'uppercase',
      },
      h2: {
        fontFamily: "'Space Mono', monospace",
        fontSize: '1.25rem',
        fontWeight: 700,
        letterSpacing: '0.06em',
        lineHeight: 1.3,
        textTransform: 'uppercase',
      },
      h3: {
        fontFamily: "'Space Mono', monospace",
        fontSize: '1rem',
        fontWeight: 600,
        letterSpacing: '0.05em',
        lineHeight: 1.3,
        textTransform: 'uppercase',
      },
      h4: {
        fontFamily: "'Space Mono', monospace",
        fontSize: '0.75rem',
        fontWeight: 500,
        letterSpacing: '0.1em',
        lineHeight: 1.4,
        textTransform: 'uppercase',
      },
      body1: {
        fontFamily: "'Space Mono', monospace",
        fontSize: '0.875rem',
        lineHeight: 1.6,
        letterSpacing: '-0.005em',
      },
      body2: {
        fontFamily: "'Space Mono', monospace",
        fontSize: '0.8rem',
        lineHeight: 1.55,
        letterSpacing: '-0.005em',
      },
      caption: {
        fontFamily: "'Space Mono', monospace",
        fontSize: '0.75rem',
        lineHeight: 1.4,
        letterSpacing: '0.01em',
        color: isDark ? 'rgba(255,255,255,0.5)' : 'rgba(0,0,0,0.45)',
      },
      button: {
        fontFamily: "'Space Mono', monospace",
        fontSize: '0.8rem',
        fontWeight: 500,
        textTransform: 'uppercase',
        letterSpacing: '0.06em',
      },
    },
    components: {
      MuiCssBaseline: {
        styleOverrides: {
          body: {
            scrollbarWidth: 'none',
            scrollbarColor: 'transparent transparent',
            '&::-webkit-scrollbar': {
              display: 'none',
            },
          },
        },
      },
      MuiButton: {
        styleOverrides: {
          root: {
            borderRadius: 6,
            fontWeight: 500,
            padding: '8px 18px',
            minHeight: 38,
            transition: 'all 0.12s ease',
            border: `1px solid ${isDark ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.12)'}`,
            boxShadow: isDark
              ? '0 2px 4px rgba(0,0,0,0.3)'
              : '0 2px 4px rgba(0,0,0,0.08)',
            '&:hover': {
              transform: 'translateY(-1px)',
              boxShadow: isDark
                ? '0 4px 8px rgba(0,0,0,0.4)'
                : '0 4px 8px rgba(0,0,0,0.12)',
            },
            '&:active': {
              transform: 'translateY(0)',
              boxShadow: isDark
                ? '0 1px 2px rgba(0,0,0,0.3)'
                : '0 1px 2px rgba(0,0,0,0.08)',
            },
          },
          sizeSmall: {
            padding: '5px 12px',
            minHeight: 32,
            fontSize: '0.75rem',
          },
          sizeLarge: {
            padding: '12px 28px',
            minHeight: 46,
            fontSize: '0.875rem',
          },
          contained: {
            backgroundColor: colors.primary,
            color: '#ffffff',
            border: 'none',
            '&:hover': {
              backgroundColor: colors.primary,
              filter: 'brightness(1.1)',
            },
          },
          outlined: {
            backgroundColor: 'transparent',
            '&:hover': {
              backgroundColor: isDark ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.04)',
            },
          },
        },
      },
      MuiCard: {
        styleOverrides: {
          root: {
            borderRadius: 8,
            border: `1px solid ${isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)'}`,
            boxShadow: isDark 
              ? '0 4px 12px rgba(0,0,0,0.4)' 
              : '0 4px 12px rgba(0,0,0,0.06)',
            backgroundImage: 'none',
            background: isDark ? '#141428' : '#e8e8e8',
            backdropFilter: 'none',
            transition: 'all 0.15s ease',
            '&:hover': {
              boxShadow: isDark 
                ? '0 6px 20px rgba(0,0,0,0.5)' 
                : '0 6px 20px rgba(0,0,0,0.1)',
              transform: 'translateY(-2px)',
              borderColor: isDark ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.12)',
            },
          },
        },
      },
      MuiChip: {
        styleOverrides: {
          root: {
            borderRadius: 4,
            fontFamily: "'Space Mono', monospace",
            fontSize: '0.72rem',
            fontWeight: 500,
            height: 26,
            letterSpacing: '0.02em',
            border: 'none',
            backgroundColor: isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)',
          },
          sizeSmall: {
            height: 22,
            fontSize: '0.68rem',
          },
          label: {
            padding: '0 10px',
          },
          labelSmall: {
            padding: '0 8px',
          },
          colorPrimary: {
            backgroundColor: `${colors.primary}20`,
            color: colors.primary,
          },
          colorSecondary: {
            backgroundColor: `${colors.secondary}20`,
            color: colors.secondary,
          },
          colorSuccess: {
            backgroundColor: isDark ? 'rgba(0,255,0,0.15)' : 'rgba(0,204,0,0.15)',
            color: isDark ? '#00ff00' : '#00aa00',
          },
          colorInfo: {
            backgroundColor: isDark ? 'rgba(0,255,255,0.15)' : 'rgba(0,153,255,0.15)',
            color: isDark ? '#00ffff' : '#0088dd',
          },
        },
      },
      MuiMenu: {
        styleOverrides: {
          paper: {
            borderRadius: 8,
            border: `1px solid ${isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.08)'}`,
            boxShadow: isDark 
              ? '0 8px 24px rgba(0,0,0,0.5)' 
              : '0 8px 24px rgba(0,0,0,0.12)',
          },
        },
      },
      MuiDialog: {
        styleOverrides: {
          paper: {
            borderRadius: 12,
            background: isDark ? '#141428' : '#e8e8e8',
            backdropFilter: 'none',
            border: `1px solid ${isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)'}`,
            boxShadow: isDark 
              ? '0 16px 48px rgba(0,0,0,0.6)' 
              : '0 16px 48px rgba(0,0,0,0.15)',
          },
        },
      },
      MuiDialogTitle: {
        styleOverrides: {
          root: {
            fontFamily: "'DotGothic16', sans-serif",
            fontSize: '1.1rem',
            fontWeight: 400,
            textTransform: 'uppercase',
            letterSpacing: '0.02em',
            padding: '20px 24px 12px',
            borderBottom: `1px solid ${isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)'}`,
          },
        },
      },
      MuiDialogContent: {
        styleOverrides: {
          root: {
            fontFamily: "'Space Mono', monospace",
            padding: '20px 24px',
            '&.MuiDialogContent-dividers': {
              borderTop: `1px solid ${isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)'}`,
              borderBottom: `1px solid ${isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)'}`,
            },
          },
        },
      },
      MuiDialogContentText: {
        styleOverrides: {
          root: {
            fontFamily: "'Space Mono', monospace",
            fontSize: '0.875rem',
            color: isDark ? 'rgba(255,255,255,0.8)' : 'rgba(0,0,0,0.7)',
            lineHeight: 1.6,
          },
        },
      },
      MuiDialogActions: {
        styleOverrides: {
          root: {
            padding: '16px 24px 20px',
            gap: '12px',
            borderTop: `1px solid ${isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)'}`,
          },
        },
      },
      MuiTextField: {
        styleOverrides: {
          root: {
            '& .MuiOutlinedInput-root': {
              fontFamily: "'Space Mono', monospace",
              fontSize: '0.875rem',
              borderRadius: 6,
              '& fieldset': {
                borderColor: isDark ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.12)',
                borderWidth: '1px',
                transition: 'all 0.15s ease',
              },
              '&:hover fieldset': {
                borderColor: isDark ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.2)',
              },
              '&.Mui-focused fieldset': {
                borderColor: colors.primary,
                borderWidth: '2px',
                boxShadow: `0 0 0 3px ${colors.primary}20`,
              },
            },
            '& .MuiInputLabel-root': {
              fontFamily: "'Space Mono', monospace",
              fontSize: '0.8rem',
              textTransform: 'uppercase',
              letterSpacing: '0.05em',
            },
            '& .MuiInputBase-input': {
              fontFamily: "'Space Mono', monospace",
              fontSize: '0.875rem',
              '&::placeholder': {
                opacity: 0.5,
              },
            },
          },
        },
      },
      MuiPaginationItem: {
        styleOverrides: {
          root: {
            borderRadius: 6,
            border: `1px solid ${isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)'}`,
            fontFamily: "'Space Mono', monospace",
            fontSize: '0.8rem',
            fontWeight: 500,
            '&.Mui-selected': {
              backgroundColor: colors.primary,
              color: '#ffffff',
              border: `1px solid ${colors.primary}`,
              '&:hover': {
                backgroundColor: colors.primary,
                filter: 'brightness(1.1)',
              },
            },
          },
        },
      },
      MuiAlert: {
        styleOverrides: {
          root: {
            borderRadius: 8,
            border: `1px solid ${isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.08)'}`,
            boxShadow: isDark 
              ? '0 4px 12px rgba(0,0,0,0.3)' 
              : '0 4px 12px rgba(0,0,0,0.06)',
            fontFamily: "'Space Mono', monospace",
            fontSize: '0.825rem',
          },
        },
      },
      MuiTableCell: {
        styleOverrides: {
          root: {
            borderBottom: `1px solid ${isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)'}`,
            fontFamily: "'Space Mono', monospace",
            fontSize: '0.78rem',
            padding: '14px 16px',
          },
          head: {
            fontFamily: "'Space Mono', monospace",
            fontSize: '0.68rem',
            fontWeight: 500,
            textTransform: 'lowercase',
            letterSpacing: '0.03em',
            color: isDark ? 'rgba(255,255,255,0.5)' : 'rgba(0,0,0,0.5)',
            backgroundColor: isDark ? 'rgba(0,0,0,0.2)' : 'rgba(0,0,0,0.03)',
            borderBottom: `1px solid ${isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)'}`,
            padding: '12px 16px',
          },
        },
      },
      MuiTableRow: {
        styleOverrides: {
          root: {
            transition: 'background-color 0.1s ease',
            '&:hover': {
              backgroundColor: isDark ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.02)',
            },
          },
        },
      },
      MuiTableContainer: {
        styleOverrides: {
          root: {
            borderRadius: 8,
            border: `1px solid ${isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)'}`,
            boxShadow: isDark 
              ? '0 4px 12px rgba(0,0,0,0.3)' 
              : '0 4px 12px rgba(0,0,0,0.04)',
          },
        },
      },
      MuiListItemButton: {
        styleOverrides: {
          root: {
            borderRadius: 10,
            margin: '2px 10px',
            padding: '10px 14px',
            transition: 'all 0.15s ease',
            '&:hover': {
              backgroundColor: isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.04)',
            },
            '&.Mui-selected': {
              backgroundColor: `${colors.primary}18`,
              boxShadow: isDark ? `0 0 20px ${colors.primary}15` : 'none',
              '&:hover': {
                backgroundColor: `${colors.primary}22`,
              },
            },
          },
        },
      },
      MuiDrawer: {
        styleOverrides: {
          paper: {
            borderRight: `1px solid ${isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)'}`,
            backgroundImage: 'none',
          },
        },
      },
      MuiDivider: {
        styleOverrides: {
          root: {
            borderColor: isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)',
          },
        },
      },
      MuiAvatar: {
        styleOverrides: {
          root: {
            borderRadius: 6,
            fontFamily: "'DotGothic16', sans-serif",
            fontSize: '0.9rem',
            fontWeight: 400,
          },
          colorDefault: {
            backgroundColor: isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.08)',
            color: isDark ? 'rgba(255,255,255,0.7)' : 'rgba(0,0,0,0.6)',
          },
        },
      },
      MuiIconButton: {
        styleOverrides: {
          root: {
            borderRadius: 6,
            transition: 'all 0.12s ease',
            '&:hover': {
              backgroundColor: isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)',
            },
          },
        },
      },
      MuiTooltip: {
        styleOverrides: {
          tooltip: {
            borderRadius: 6,
            backgroundColor: isDark ? '#1a1a30' : '#333',
            color: '#ffffff',
            fontFamily: "'Space Mono', monospace",
            fontSize: '0.75rem',
            fontWeight: 400,
            padding: '6px 10px',
            boxShadow: isDark 
              ? '0 4px 12px rgba(0,0,0,0.4)' 
              : '0 4px 12px rgba(0,0,0,0.15)',
          },
        },
      },
      MuiSkeleton: {
        styleOverrides: {
          root: {
            borderRadius: 4,
            backgroundColor: isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)',
          },
        },
      },
      MuiBreadcrumbs: {
        styleOverrides: {
          root: {
            fontFamily: "'Space Mono', monospace",
            fontSize: '0.75rem',
          },
          separator: {
            color: isDark ? 'rgba(255,255,255,0.3)' : 'rgba(0,0,0,0.3)',
          },
        },
      },
      MuiLink: {
        styleOverrides: {
          root: {
            fontFamily: "'Space Mono', monospace",
            textDecoration: 'none',
            color: isDark ? 'rgba(255,255,255,0.6)' : 'rgba(0,0,0,0.55)',
            transition: 'color 0.12s ease',
            '&:hover': {
              color: colors.primary,
            },
          },
        },
      },
      MuiCheckbox: {
        styleOverrides: {
          root: {
            color: isDark ? 'rgba(255,255,255,0.3)' : 'rgba(0,0,0,0.3)',
            '&.Mui-checked': {
              color: colors.primary,
            },
          },
        },
      },
    },
  });
};

export default getTheme;
